import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FaBan, FaCar, FaCheck, FaClock, FaExclamationTriangle, FaHourglassHalf, FaListUl, FaRegCalendarCheck, FaReceipt, FaExternalLinkAlt } from 'react-icons/fa';
import { MdListAlt, MdPending } from 'react-icons/md';
import { getCarDashboard, getCarDrilldown, exportCarDrilldown } from 'redux/booking/dashboard/carSlice';
import { getCarTransactions, getCarTransactionsPending } from 'redux/booking/car/carBookingSlice';
import { ReportDateRangePicker, LoadingSpinner, Badge, Card, Tabs } from 'components/common';
import { StatTile, BarList, DonutChart, YoYChart, BookingList, BookingTable, SiteFilter } from 'components/app/bookings/dashboard/DashboardBits';
import DrilldownDrawer from 'components/app/bookings/dashboard/DrilldownDrawer';
import CarTransactionGrid from 'components/app/bookings/car/report/car_transaction/component/CarTransactionGrid';
import CarCalendar from 'components/app/bookings/car/CarCalendar';

const LINK_BASE = '/booking/car';
const fmtDateTime = (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '');

const CarLinkRenderer = (p) => (
  <Link to={`${LINK_BASE}/${p.data?.id}`} target="_blank" className="tw-text-blue-600 hover:tw-underline tw-inline-flex tw-items-center tw-gap-1">
    {p.value} <FaExternalLinkAlt size={8} className="tw-opacity-60" />
  </Link>
);

const DRILL_COLUMN_DEFS = [
  { headerName: 'Booking', field: 'booking_number', pinned: 'left', minWidth: 140, cellRenderer: CarLinkRenderer },
  { headerName: 'Purpose', field: 'title', minWidth: 220 },
  { headerName: 'From date', field: 'from_date', minWidth: 160, valueFormatter: (p) => fmtDateTime(p.value) },
  { headerName: 'Site', field: 'site_name', minWidth: 140 },
  { headerName: 'Status', field: 'status_name', minWidth: 150 },
];

const CarBookingDashboardPage = () => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector((s) => s.booking_dashboard_car);
  const [range, setRange] = useState({ startDate: dayjs().startOf('year').format('YYYY-MM-DD'), endDate: dayjs().endOf('year').format('YYYY-MM-DD') });
  const [siteIds, setSiteIds] = useState(null); // null = not yet seeded
  const [drill, setDrill] = useState({ open: false, item: null });

  const {
    stats = {}, yoy = {}, sites = [], by_status = [], by_site = [], by_driver = [], by_car = [],
    this_week = [], upcoming = [], urgent = [], stale_pending = [], transactions = [],
  } = data || {};

  // Both empty and full selections mean "all sites" → no filter.
  const siteParam = () => {
    const v = siteIds || [];
    return v.length && sites.length && v.length < sites.length ? { site_id: v } : {};
  };
  const params = { from_date: range.startDate, end_date: range.endDate, ...siteParam() };

  useEffect(() => {
    dispatch(getCarDashboard({ from_date: range.startDate, end_date: range.endDate, ...siteParam() }));
    // siteParam reads `sites` from the response itself; depending on it would refetch on every response — siteIds covers the user's selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, range, siteIds]);

  // Default = all sites ticked once the site list is known.
  useEffect(() => {
    if (siteIds === null && sites.length) setSiteIds(sites.map((s) => s.value));
  }, [sites, siteIds]);

  const openDrill = useCallback((item) => setDrill({ open: true, item }), []);
  const openStat = (key, label, value) => openDrill({ dimension: 'stat', key, label, value });

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
        <div className="tw-flex tw-items-center tw-gap-2">
          <FaCar className="tw-text-blue-600" size={20} />
          <div>
            <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Car Booking Dashboard</h1>
            <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Bookings by status, driver, car, site & upcoming — click any bar for details.</p>
          </div>
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-rounded-lg tw-p-3 tw-shadow-sm">
        <ReportDateRangePicker startDate={range.startDate} endDate={range.endDate} onChange={setRange} />
        <SiteFilter sites={sites} value={siteIds || []} onChange={setSiteIds} />
        {loading && <LoadingSpinner />}
      </div>

      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-3">
        <StatTile label="Total bookings" value={stats.total} icon={FaListUl} delta={yoy.delta_pct} onClick={() => openStat('total', 'Total bookings', stats.total)} />
        <StatTile label="Pending" value={stats.pending} icon={FaClock} accent="tw-text-amber-600" bg="tw-bg-amber-50" onClick={() => openStat('pending', 'Pending', stats.pending)} />
        <StatTile label="Approved" value={stats.approved} icon={FaCheck} accent="tw-text-green-600" bg="tw-bg-green-50" onClick={() => openStat('approved', 'Approved', stats.approved)} />
        <StatTile label="Canceled" value={stats.canceled} icon={FaBan} accent="tw-text-red-600" bg="tw-bg-red-50" onClick={() => openStat('canceled', 'Canceled', stats.canceled)} />
        <StatTile label="This week" value={stats.this_week} icon={FaRegCalendarCheck} accent="tw-text-primary-600" bg="tw-bg-primary-50" onClick={() => openStat('this_week', 'This week', stats.this_week)} />
        <StatTile label="Urgent to approve" value={stats.urgent} icon={FaExclamationTriangle} accent="tw-text-red-600" bg="tw-bg-red-50" highlight={stats.urgent > 0} onClick={() => openStat('urgent', 'Urgent to approve', stats.urgent)} />
      </div>

      {(urgent.length > 0 || stale_pending.length > 0) && (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
          <BookingList title="Urgent — departing soon, not yet approved" items={urgent} dateField="from_date" metaField="site_name" variant="urgent" icon={FaExclamationTriangle} linkBase={LINK_BASE} emptyText="No urgent bookings" />
          <BookingList title="Long pending / missed" items={stale_pending} dateField="from_date" metaField="site_name" variant="highlight" icon={FaHourglassHalf} linkBase={LINK_BASE} emptyText="Nothing overdue" />
        </div>
      )}

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        <DonutChart title="By status" items={by_status} onItemClick={openDrill} />
        <BarList title="By site office" items={by_site} onItemClick={openDrill} />
      </div>
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        <BarList title="Top drivers (bookings · trips · km · hrs)" items={by_driver} onItemClick={openDrill} renderMeta={(i) => ` · ${i.trips ?? 0} trips · ${i.total_km ?? 0}km · ${i.total_hours ?? 0}h`} />
        <BarList title="Top cars (bookings · trips · km)" items={by_car} onItemClick={openDrill} renderMeta={(i) => ` · ${i.trips ?? 0} trips · ${i.total_km ?? 0}km`} />
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        <YoYChart title="This year vs last year" yoy={yoy} />
        <BookingList title="This week" items={this_week} dateField="from_date" metaField="site_name" linkBase={LINK_BASE} emptyText="No bookings this week" />
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        <BookingList title="Upcoming (30 days)" items={upcoming} dateField="from_date" metaField="site_name" linkBase={LINK_BASE} emptyText="No upcoming bookings" />
        <BookingTable
          title="Recent driver transactions"
          rows={transactions}
          linkBase={LINK_BASE}
          linkKey="booking_id"
          linkCol="booking_number"
          columns={[
            { key: 'booking_number', label: 'Booking' },
            { key: 'driver', label: 'Driver' },
            { key: 'car', label: 'Car' },
            { key: 'total_kilometers', label: 'Km', align: 'right' },
            { key: 'total_hours', label: 'Hrs', align: 'right' },
            { key: 'confirmed', label: 'Confirmed', render: (r) => (r.confirmed ? <Badge variant="success" size="sm">Yes</Badge> : <Badge variant="warning" size="sm">No</Badge>) },
          ]}
          emptyText="No transactions"
        />
      </div>

      {/* Booking calendar, centralized in the dashboard */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
        <FaRegCalendarCheck className="tw-text-blue-600" size={16} />
        <h2 className="tw-mb-0 tw-font-bold tw-text-lg tw-text-gray-900">Booking calendar</h2>
      </div>
      <div className="-tw-mt-1"><CarCalendar /></div>

      {/* Full car-transactions report, moved here from the standalone report page */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
        <FaReceipt className="tw-text-blue-600" size={16} />
        <h2 className="tw-mb-0 tw-font-bold tw-text-lg tw-text-gray-900">Car booking transactions</h2>
        <span className="tw-text-gray-400 tw-text-sm">Driver-trip transactions across all car bookings.</span>
      </div>
      <Card padding="none">
        <Tabs
          variant="underline"
          lazyMount
          navClassName="tw-px-3 tw-pt-2 tw-bg-white"
          contentClassName="tw-bg-slate-50 tw-p-4"
          tabs={[
            { label: 'All', icon: <MdListAlt size={14} />, content: <CarTransactionGrid stateKey="carTransactionData" loadThunk={getCarTransactions} exportStatus="all" /> },
            { label: 'Waiting for confirmation this month', icon: <MdPending size={14} />, content: <CarTransactionGrid stateKey="carTransactionPendingData" loadThunk={getCarTransactionsPending} exportStatus="pending" /> },
          ]}
        />
      </Card>

      <DrilldownDrawer
        show={drill.open}
        onClose={() => setDrill({ open: false, item: null })}
        item={drill.item}
        baseParams={params}
        fetchThunk={getCarDrilldown}
        exportThunk={exportCarDrilldown}
        columnDefs={DRILL_COLUMN_DEFS}
        unit="booking"
      />
    </div>
  );
};

export default CarBookingDashboardPage;
